import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sun, Cloud, CloudRain, Users, TrendingUp, Target, Trophy, Calendar, MapPin, Clock } from 'lucide-react';
import dayjs from "dayjs";
import Countdown from "../Match/Countdown";
import { Star } from 'lucide-react';
import useInView from '../../hooks/useInView';
import { getTeamLogo, getTeamInitial } from '../../utils/teamLogos';

export default function ClassementEtMatchsSection({ classement = [], dernierMatch, prochainMatch, loading = false }) {
  // Refs pour les animations au scroll
  const [headerRef, headerInView] = useInView({ threshold: 0.2 });
  const [classementRef, classementInView] = useInView({ threshold: 0.2 });
  const [matchsRef, matchsInView] = useInView({ threshold: 0.2 });

  // Protection: s'assurer que classement est un tableau
  const classementSafe = Array.isArray(classement) ? classement : [];

  // Trouver la ligne EFAFC pour le surlignage
  const efafcIndex = classementSafe.findIndex(team =>
    team?.equipe?.short_name_federation?.toLowerCase().includes("feignies") ||
    team?.equipe?.short_name_federation?.toLowerCase().includes("aulnoye") ||
    team?.equipe?.short_name_federation?.toLowerCase().includes("efafc")
  );

  // Fonction pastille couleur selon position
  const getBadgeColor = (rank) => {
    if (rank === 1) return "bg-gradient-to-br from-[#FFD700] to-[#FFED4E] text-[#001B56]";
    if (rank <= 3) return "bg-gradient-to-br from-[#BDFF00] to-[#A5E000] text-[#001B56]";
    if (rank >= classementSafe.length - 2) return "bg-gradient-to-br from-red-500 to-red-600 text-white";
    return "bg-gradient-to-br from-gray-400 to-gray-500 text-white";
  };

  // Fonction pour obtenir une icône météo aléatoire
  const getWeatherIcon = () => {
    const icons = [
      { Icon: Sun, temp: "18°C", desc: "Ensoleillé" },
      { Icon: Cloud, temp: "15°C", desc: "Nuageux" },
      { Icon: CloudRain, temp: "12°C", desc: "Pluvieux" }
    ];
    return icons[Math.floor(Math.random() * icons.length)];
  };

  return (
    <section className="py-16 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Header */}
        <motion.div
          ref={headerRef}
          className="text-center mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-title font-extrabold text-white mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={headerInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.8, delay: headerInView ? 0.2 : 0 }}
          >
            CLASSEMENT
            <motion.span
              className="block text-[#FFE400]"
              initial={{ opacity: 0, x: -50 }}
              animate={headerInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.8, delay: headerInView ? 0.4 : 0 }}
            >
              & MATCHS
            </motion.span>
          </motion.h2>
          <motion.div
            className="w-24 h-1 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] mx-auto rounded-full"
            initial={{ scaleX: 0 }}
            animate={headerInView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 0.8, delay: headerInView ? 0.6 : 0 }}
          ></motion.div>
          <motion.p
            className="text-[#FFE400]/80 mt-4 font-accent text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: headerInView ? 0.8 : 0 }}
          >
            National 2 - Poule B • Saison 2025/2026
          </motion.p>
        </motion.div>

        {/* Grille principale */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* CLASSEMENT */}
          <motion.div
            ref={classementRef}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-[#FFE400]/20"
            initial={{ opacity: 0, x: -100 }}
            animate={classementInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-title font-bold text-white flex items-center">
                <Trophy className="text-[#FFE400] w-6 h-6 mr-3" />
                Classement N2
              </h3>
              <div className="flex items-center gap-2 bg-[#FFE400]/10 rounded-full px-3 py-1">
                <div className="w-2 h-2 bg-[#BDFF00] rounded-full animate-pulse" />
                <span className="text-[#BDFF00] text-sm font-accent">En direct</span>
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto classement-scroll">
              {classementSafe.length > 0 ? (
                classementSafe.map((team, idx) => (
                  <ClassementRow
                    key={team?.["@id"] || `team-${idx}`}
                    team={team}
                    isEfafc={idx === efafcIndex}
                    getBadgeColor={getBadgeColor}
                    index={idx}
                    inView={classementInView}
                  />
                ))
              ) : (
                <div className="text-center text-white/60 py-8">
                  <p>Chargement du classement...</p>
                </div>
              )}
            </div>

            {/* Statistiques rapides du classement */}
            <div className="mt-6 pt-6 border-t border-[#FFE400]/20">
              <div className="grid grid-cols-3 gap-4">
                <motion.div
                  className="bg-[#FFE400]/10 rounded-xl p-3 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={classementInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: classementInView ? 0.8 : 0 }}
                >
                  <div className="text-[#BDFF00] text-lg font-title font-bold">{classement.length}</div>
                  <div className="text-[#FFE400]/80 text-xs font-accent">Équipes</div>
                </motion.div>
                <motion.div
                  className="bg-[#FFE400]/10 rounded-xl p-3 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={classementInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: classementInView ? 0.9 : 0 }}
                >
                  <div className="text-[#BDFF00] text-lg font-title font-bold">
                    {efafcIndex !== -1 ? efafcIndex + 1 : '-'}
                  </div>
                  <div className="text-[#FFE400]/80 text-xs font-accent">Position EFAFC</div>
                </motion.div>
                <motion.div
                  className="bg-[#FFE400]/10 rounded-xl p-3 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={classementInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: classementInView ? 1.0 : 0 }}
                >
                  <div className="text-[#BDFF00] text-lg font-title font-bold">
                    {efafcIndex !== -1 ? classement[efafcIndex]?.point_count : '-'}
                  </div>
                  <div className="text-[#FFE400]/80 text-xs font-accent">Points</div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* MATCHS */}
          <motion.div
            ref={matchsRef}
            className="space-y-6"
            initial={{ opacity: 0, x: 100 }}
            animate={matchsInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 100 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Dernier match */}
            <DernierMatchCard dernierMatch={dernierMatch} inView={matchsInView} loading={loading} />

            {/* Prochain match */}
            <ProchainMatchCard prochainMatch={prochainMatch} getWeatherIcon={getWeatherIcon} inView={matchsInView} loading={loading} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Composant DernierMatchCard refactorisé
function DernierMatchCard({ dernierMatch, inView, loading = false }) {
  // Si en cours de chargement, afficher un skeleton
  if (loading) {
    return (
      <motion.div
        className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#FFE400]/20"
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <h3 className="text-xl font-title font-bold text-white mb-4 flex items-center">
          <Calendar className="text-[#FFE400] w-5 h-5 mr-2" />
          Dernier match
        </h3>
        <div className="text-center py-8">
          <div className="text-4xl mb-4 animate-pulse">⚽</div>
          <div className="text-[#FFE400] font-accent animate-pulse">Chargement des données FFF...</div>
        </div>
      </motion.div>
    );
  }

  if (!dernierMatch) {
    return (
      <motion.div
        className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#FFE400]/20"
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <h3 className="text-xl font-title font-bold text-white mb-4 flex items-center">
          <Calendar className="text-[#FFE400] w-5 h-5 mr-2" />
          Dernier match
        </h3>
        <div className="text-center py-8">
          <div className="text-4xl mb-4">📅</div>
          <div className="text-white font-accent">Aucun match récent</div>
        </div>
      </motion.div>
    );
  }

  // Déterminer si FEIGNIES/AULNOYE est à domicile ou à l'extérieur
  const isEfafcHome = dernierMatch.home.short_name_federation?.toLowerCase().includes('feignies') ||
    dernierMatch.home.short_name_federation?.toLowerCase().includes('aulnoye');
  const isEfafcAway = dernierMatch.away.short_name_federation?.toLowerCase().includes('feignies') ||
    dernierMatch.away.short_name_federation?.toLowerCase().includes('aulnoye');

  // Calculer le résultat pour EFAFC
  let resultLabel = '';
  let resultColor = '';

  if (dernierMatch.home_score === dernierMatch.away_score) {
    resultLabel = 'Match nul';
    resultColor = 'text-[#FFE400]';
  } else if (isEfafcHome) {
    // EFAFC à domicile
    if (dernierMatch.home_score > dernierMatch.away_score) {
      resultLabel = 'Victoire';
      resultColor = 'text-[#BDFF00]';
    } else {
      resultLabel = 'Défaite';
      resultColor = 'text-red-400';
    }
  } else if (isEfafcAway) {
    // EFAFC à l'extérieur
    if (dernierMatch.away_score > dernierMatch.home_score) {
      resultLabel = 'Victoire';
      resultColor = 'text-[#BDFF00]';
    } else {
      resultLabel = 'Défaite';
      resultColor = 'text-red-400';
    }
  }

  return (
    <motion.div
      className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#FFE400]/20"
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: 0.6 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-title font-bold text-white flex items-center">
          <Calendar className="text-[#FFE400] w-5 h-5 mr-2" />
          Dernier match
        </h3>
        <div className="bg-[#FFE400]/10 rounded-full px-3 py-1">
          <span className="text-[#FFE400] text-sm font-accent">Terminé</span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        {/* Équipe domicile */}
        <div className="flex flex-col items-center flex-1">
          {getTeamLogo(dernierMatch.home.short_name_federation) ? (
            <img
              src={getTeamLogo(dernierMatch.home.short_name_federation)}
              alt={dernierMatch.home.short_name}
              className="h-12 w-12 object-contain mb-2"
            />
          ) : (
            <div className="h-12 w-12 bg-[#FFE400]/20 rounded-full flex items-center justify-center mb-2">
              <span className="text-[#FFE400] font-bold">
                {dernierMatch.home.short_name_federation?.charAt(0)}
              </span>
            </div>
          )}
          <span className="text-[#FFE400] text-xs font-accent text-center">
            {dernierMatch.home.short_name_federation}
          </span>
        </div>

        {/* Score */}
        <div className="text-center mx-4">
          <div className="text-2xl font-title font-bold text-white">
            {dernierMatch.home_score} - {dernierMatch.away_score}
          </div>
          {resultLabel && (
            <div className={`text-sm font-accent font-bold ${resultColor}`}>
              {resultLabel}
            </div>
          )}
        </div>

        {/* Équipe extérieur */}
        <div className="flex flex-col items-center flex-1">
          {getTeamLogo(dernierMatch.away.short_name_federation) ? (
            <img
              src={getTeamLogo(dernierMatch.away.short_name_federation)}
              alt={dernierMatch.away.short_name}
              className="h-12 w-12 object-contain mb-2"
            />
          ) : (
            <div className="h-12 w-12 bg-[#FFE400]/20 rounded-full flex items-center justify-center mb-2">
              <span className="text-[#FFE400] font-bold">
                {dernierMatch.away.short_name_federation?.charAt(0)}
              </span>
            </div>
          )}
          <span className="text-[#FFE400] text-xs font-accent text-center">
            {dernierMatch.away.short_name_federation}
          </span>
        </div>
      </div>

      <div className="text-center space-y-2">
        <div className="text-white text-sm font-accent">
          {dayjs(dernierMatch.date).format("DD MMMM YYYY")} à {dernierMatch.time}
        </div>
        <div className="text-[#FFE400]/80 text-xs font-accent flex items-center justify-center gap-1">
          <MapPin size={12} />
          {dernierMatch.terrain?.name} - {dernierMatch.terrain?.city}
        </div>
        {/* Affichage de la compétition */}
        {dernierMatch.competition?.name && (
          <div className="inline-block bg-[#001B56]/50 border border-[#FFE400]/30 rounded-full px-3 py-1 mt-2">
            <span className="text-[#FFE400] text-xs font-accent font-bold">
              🏆 {dernierMatch.competition.name}
            </span>
          </div>
        )}

        {/* Arbitres */}
        {dernierMatch.match_membres && dernierMatch.match_membres.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#FFE400]/10">
            <p className="text-[#FFE400]/60 text-xs font-accent mb-2">Officiels du match</p>
            <div className="flex flex-wrap justify-center gap-2">
              {dernierMatch.match_membres.map((membre, idx) => (
                <div key={idx} className="bg-[#001B56]/30 px-2 py-1 rounded text-xs text-white/80 font-accent">
                  {membre.type_membre}: {membre.prenom} {membre.nom}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Composant ProchainMatchCard refactorisé
function ProchainMatchCard({ prochainMatch, getWeatherIcon, inView, loading = false }) {
  // Si en cours de chargement, afficher un skeleton
  if (loading) {
    return (
      <motion.div
        className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#BDFF00]/20"
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <h3 className="text-xl font-title font-bold text-white mb-4 flex items-center">
          <Clock className="text-[#BDFF00] w-5 h-5 mr-2" />
          Prochain match
        </h3>
        <div className="text-center py-8">
          <div className="text-4xl mb-4 animate-pulse">⏳</div>
          <div className="text-[#BDFF00] font-accent animate-pulse">Chargement du calendrier...</div>
        </div>
      </motion.div>
    );
  }

  if (!prochainMatch) {
    return (
      <motion.div
        className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#BDFF00]/20"
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <h3 className="text-xl font-title font-bold text-white mb-4 flex items-center">
          <Clock className="text-[#BDFF00] w-5 h-5 mr-2" />
          Prochain match
        </h3>
        <div className="text-center py-8">
          <div className="text-4xl mb-4">⚽</div>
          <div className="text-white font-accent">Pas de match à venir</div>
          <div className="text-[#FFE400]/80 text-sm font-accent mt-2">
            Les prochains matchs seront bientôt programmés
          </div>
        </div>
      </motion.div>
    );
  }

  const weather = getWeatherIcon();

  return (
    <motion.div
      className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-[#BDFF00]/20"
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: 0.8 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-title font-bold text-white flex items-center">
          <Clock className="text-[#BDFF00] w-5 h-5 mr-2" />
          Prochain match
        </h3>
        <div className="flex items-center gap-2 bg-[#BDFF00]/10 rounded-full px-3 py-1">
          <weather.Icon size={14} className="text-[#BDFF00]" />
          <span className="text-[#BDFF00] text-sm font-accent">{weather.temp}</span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        {/* Équipe domicile */}
        <div className="flex flex-col items-center flex-1">
          {getTeamLogo(prochainMatch.home.short_name_federation) ? (
            <img
              src={getTeamLogo(prochainMatch.home.short_name_federation)}
              alt={prochainMatch.home.short_name_federation}
              className="h-12 w-12 object-contain mb-2"
            />
          ) : (
            <div className="h-12 w-12 bg-[#FFE400]/20 rounded-full flex items-center justify-center mb-2">
              <span className="text-[#FFE400] font-bold">
                {prochainMatch.home.short_name_federation?.charAt(0)}
              </span>
            </div>
          )}
          <span className="text-[#FFE400] text-xs font-accent text-center">
            {prochainMatch.home.short_name_federation}
          </span>
        </div>

        {/* VS */}
        <div className="text-center mx-4">
          <div className="text-xl font-title font-bold text-white bg-gradient-to-r from-[#FFE400] to-[#BDFF00] bg-clip-text text-transparent">
            VS
          </div>
        </div>

        {/* Équipe extérieur */}
        <div className="flex flex-col items-center flex-1">
          {getTeamLogo(prochainMatch.away.short_name_federation) ? (
            <img
              src={getTeamLogo(prochainMatch.away.short_name_federation)}
              alt={prochainMatch.away.short_name_federation}
              className="h-12 w-12 object-contain mb-2"
            />
          ) : (
            <div className="h-12 w-12 bg-[#FFE400]/20 rounded-full flex items-center justify-center mb-2">
              <span className="text-[#FFE400] font-bold">
                {prochainMatch.away.short_name_federation?.charAt(0)}
              </span>
            </div>
          )}
          <span className="text-[#FFE400] text-xs font-accent text-center">
            {prochainMatch.away.short_name_federation}
          </span>
        </div>
      </div>

      <div className="text-center mb-4">
        <div className="text-white text-sm font-accent mb-1">
          {dayjs(prochainMatch.date).format("DD MMMM YYYY")} à {prochainMatch.time}
        </div>
        <div className="text-[#FFE400]/80 text-xs font-accent flex items-center justify-center gap-1 mb-3">
          <MapPin size={12} />
          {prochainMatch.terrain?.name || "Stade non spécifié"} - {prochainMatch.terrain?.city || "?"}
        </div>

        <div className="mb-4">
          <Countdown targetDate={`${prochainMatch.date.split("T")[0]}T${prochainMatch.time.replace("H", ":")}`} />
        </div>

        {/* Type de surface */}
        {prochainMatch.terrain?.surface && (
          <div className="mb-4">
            <span className="text-[#BDFF00]/80 text-xs font-accent border border-[#BDFF00]/30 px-2 py-1 rounded-full">
              🌱 {prochainMatch.terrain.surface}
            </span>
          </div>
        )}
      </div>

      <div className="text-center flex flex-col gap-3">
        <Link to="/matchs">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] px-6 py-2 rounded-xl font-accent font-bold text-sm shadow-lg hover:shadow-xl transition-all duration-300 w-full"
          >
            <span className="flex items-center justify-center gap-2">
              <Target size={16} />
              Voir les matchs
            </span>
          </motion.button>
        </Link>

        {/* Bouton Itinéraire */}
        {(prochainMatch.terrain?.address || prochainMatch.terrain?.city) && (
          <motion.a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              `${prochainMatch.terrain.name || ''} ${prochainMatch.terrain.address || ''} ${prochainMatch.terrain.zip_code || ''} ${prochainMatch.terrain.city || ''}`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-[#001B56] border border-[#BDFF00]/30 text-[#BDFF00] px-6 py-2 rounded-xl font-accent font-bold text-sm shadow-lg hover:shadow-xl transition-all duration-300 w-full flex items-center justify-center gap-2"
          >
            <MapPin size={16} />
            Y aller
          </motion.a>
        )}
      </div>
    </motion.div>
  );
}

// Composant ClassementRow refactorisé
function ClassementRow({ team, isEfafc, getBadgeColor, index, inView }) {
  // Protection contre les données nulles
  if (!team || !team.equipe) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
      transition={{ delay: index * 0.05 }}
      className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-300 group relative
        ${isEfafc
          ? 'bg-gradient-to-r from-[#FFE400]/20 to-[#BDFF00]/20 border border-[#FFE400]/50 shadow-lg'
          : 'hover:bg-white/5 hover:scale-[1.02] cursor-pointer'
        }
      `}
    >
      {/* Badge de position */}
      <motion.span
        whileHover={{ scale: 1.1 }}
        className={`w-8 h-8 flex items-center justify-center rounded-full font-title font-bold text-sm mr-3 shadow-lg ${getBadgeColor(team.rank || 0)}`}
      >
        {team.rank || '-'}
      </motion.span>

      {/* Informations de l'équipe */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Étoile pour EFAFC */}
        {isEfafc && (
          <motion.div
            animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Star size={16} className="text-[#FFE400]" />
          </motion.div>
        )}

        {/* Logo de l'équipe */}
        {getTeamLogo(team.equipe.short_name_federation) && (
          <motion.img
            whileHover={{ scale: 1.2 }}
            src={getTeamLogo(team.equipe.short_name_federation)}
            alt={team.equipe.short_name_federation}
            className="h-6 w-6 object-contain"
          />
        )}

        {/* Nom de l'équipe */}
        <span className={`font-accent font-medium text-sm group-hover:text-[#BDFF00] transition-colors truncate ${isEfafc ? 'text-[#FFE400] font-bold' : 'text-white'
          }`}>
          {team.equipe.short_name_federation}
        </span>
      </div>

      {/* Points */}
      <motion.span
        whileHover={{ scale: 1.1 }}
        className={`font-title font-bold text-lg px-2 py-1 rounded-lg ${isEfafc ? 'text-[#FFE400] bg-[#FFE400]/10' : 'text-[#BDFF00]'
          }`}
      >
        {team.point_count}
      </motion.span>

      {/* Effet de fond animé pour EFAFC */}
      {isEfafc && (
        <motion.div
          animate={{ opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 bg-gradient-to-r from-[#FFE400]/10 to-[#BDFF00]/10 rounded-xl pointer-events-none"
        />
      )}
    </motion.div>
  );
} 